/**
 * Bed generator — a standard bed frame built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width  (head-to-foot is along Y)
 *   - Y = length (foot to head)
 *   - Z = height
 *
 * Preset sizes (innerW × innerL, standard mattress dimensions):
 *   - twin:   0.97 × 1.91 m
 *   - full:   1.37 × 1.91 m
 *   - queen:  1.52 × 2.03 m  (default)
 *   - king:   1.93 × 2.03 m
 *   - custom: user-defined width × length
 *
 * Structure (all box geometry):
 *   - Mattress platform (top of frame)
 *   - Headboard panel at +Y end
 *   - Optional footboard panel at -Y end
 *   - Four corner legs
 *   - Two side rails connecting the legs
 */

import type { BoxSpec } from "../generators/generatorGeom.ts";
import { concatBoxes } from "../generators/generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface BedParams extends GeneratorParams {
  sizePreset: string;
  mattressWidth: number;
  mattressLength: number;
  frameHeight: number;
  mattressThickness: number;
  legSize: number;
  railThickness: number;
  headboardHeight: number;
  headboardThickness: number;
  hasFootboard: boolean;
  footboardHeight: number;
}

const BED_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "bed-size",
    label: "Size",
    params: [
      {
        id: "sizePreset",
        label: "Preset",
        type: "enum",
        default: "queen",
        options: [
          { value: "twin", label: "Twin (0.97 × 1.91 m)" },
          { value: "full", label: "Full (1.37 × 1.91 m)" },
          { value: "queen", label: "Queen (1.52 × 2.03 m)" },
          { value: "king", label: "King (1.93 × 2.03 m)" },
          { value: "custom", label: "Custom" },
        ],
      },
      {
        id: "mattressWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 1.52,
        min: 0.6,
        max: 2.5,
        step: 0.01,
        showIf: { id: "sizePreset", value: "custom" },
      },
      {
        id: "mattressLength",
        label: "Length",
        type: "number",
        unit: "meters",
        default: 2.03,
        min: 1.2,
        max: 3.0,
        step: 0.01,
        showIf: { id: "sizePreset", value: "custom" },
      },
    ],
  },
  {
    type: "section",
    id: "bed-frame",
    label: "Frame",
    params: [
      {
        id: "frameHeight",
        label: "Frame Height",
        type: "number",
        unit: "meters",
        default: 0.4,
        min: 0.1,
        max: 0.8,
        step: 0.01,
      },
      {
        id: "mattressThickness",
        label: "Mattress Thickness",
        type: "number",
        unit: "meters",
        default: 0.24,
        min: 0.1,
        max: 0.4,
        step: 0.01,
      },
      {
        id: "legSize",
        label: "Leg Size",
        type: "number",
        unit: "meters",
        default: 0.07,
        min: 0.03,
        max: 0.15,
        step: 0.005,
      },
      {
        id: "railThickness",
        label: "Rail Thickness",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0.02,
        max: 0.1,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "bed-headboard",
    label: "Headboard",
    params: [
      {
        id: "headboardHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.8,
        min: 0.2,
        max: 1.5,
        step: 0.05,
      },
      {
        id: "headboardThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.08,
        min: 0.03,
        max: 0.2,
        step: 0.01,
      },
    ],
  },
  {
    type: "section",
    id: "bed-footboard",
    label: "Footboard",
    defaultCollapsed: true,
    params: [
      {
        id: "hasFootboard",
        label: "Footboard",
        type: "boolean",
        default: false,
      },
      {
        id: "footboardHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.4,
        min: 0.1,
        max: 1.0,
        step: 0.05,
        showIf: { id: "hasFootboard", value: true },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Preset mattress dimensions
// ---------------------------------------------------------------------------

const PRESET_SIZES: Record<string, [number, number]> = {
  twin: [0.97, 1.91],
  full: [1.37, 1.91],
  queen: [1.52, 2.03],
  king: [1.93, 2.03],
};

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function buildBed(p: BedParams, _ctx: GeneratorBuildContext): GeneratorOutput {
  const preset = enumParam(p, "sizePreset", "queen");
  const [presetW, presetL] = PRESET_SIZES[preset] ?? [1.52, 2.03];

  const mattW =
    preset === "custom" ? numParam(p, "mattressWidth", 1.52) : presetW;
  const mattL =
    preset === "custom" ? numParam(p, "mattressLength", 2.03) : presetL;
  const frameH = numParam(p, "frameHeight", 0.4);
  const mattT = numParam(p, "mattressThickness", 0.24);
  const legSz = numParam(p, "legSize", 0.07);
  const railT = numParam(p, "railThickness", 0.05);
  const headH = numParam(p, "headboardHeight", 0.8);
  const headT = numParam(p, "headboardThickness", 0.08);
  const hasFoot = boolParam(p, "hasFootboard", false);
  const footH = numParam(p, "footboardHeight", 0.4);

  const boxes: BoxSpec[] = [];

  // Frame outer dimensions = mattress + rail thickness on each side
  const frameW = mattW + railT * 2;
  const frameL = mattL + railT * 2;

  // Corner legs — all four stay at frame height so leg size only affects the
  // base supports, not the headboard silhouette.
  const legXOff = frameW * 0.5 - legSz * 0.5;
  const legYOff = frameL * 0.5 - legSz * 0.5;
  for (const sx of [1, -1]) {
    for (const sy of [1, -1]) {
      boxes.push({
        cx: sx * legXOff,
        cy: sy * legYOff,
        cz: frameH * 0.5,
        sx: legSz,
        sy: legSz,
        sz: frameH,
      });
    }
  }

  // Rails hang below the top of the frame instead of running to the floor, so
  // the corner legs remain visibly distinct beneath the frame on all corners.
  const railH = Math.min(frameH, Math.max(0.08, frameH * 0.4));
  const railYSpan = frameL - legSz * 2;
  const railCZ = frameH - railH * 0.5;
  boxes.push({
    cx: frameW * 0.5 - railT * 0.5,
    cy: 0,
    cz: railCZ,
    sx: railT,
    sy: railYSpan,
    sz: railH,
  });
  boxes.push({
    cx: -frameW * 0.5 + railT * 0.5,
    cy: 0,
    cz: railCZ,
    sx: railT,
    sy: railYSpan,
    sz: railH,
  });

  // End rails — connecting the legs at head and foot ends
  const endRailXSpan = frameW - legSz * 2;
  boxes.push({
    cx: 0,
    cy: frameL * 0.5 - railT * 0.5,
    cz: railCZ,
    sx: endRailXSpan,
    sy: railT,
    sz: railH,
  });
  boxes.push({
    cx: 0,
    cy: -frameL * 0.5 + railT * 0.5,
    cz: railCZ,
    sx: endRailXSpan,
    sy: railT,
    sz: railH,
  });

  // Platform slat (mattress base)
  const platformT = 0.04;
  boxes.push({
    cx: 0,
    cy: 0,
    cz: frameH + platformT * 0.5,
    sx: mattW,
    sy: mattL,
    sz: platformT,
  });

  // Mattress — sits on the platform
  const mattCZ = frameH + platformT + mattT * 0.5;
  boxes.push({
    cx: 0,
    cy: 0,
    cz: mattCZ,
    sx: mattW,
    sy: mattL,
    sz: mattT,
  });

  // Headboard posts — separate from the bed legs so the headboard no longer
  // mutates when the leg size changes.
  const boardPostSz = Math.max(railT, headT);
  const boardPostX = frameW * 0.5 - boardPostSz * 0.5;
  const headPostY = frameL * 0.5 - boardPostSz * 0.5;
  const headPostH = frameH + headH;
  for (const sx of [1, -1]) {
    boxes.push({
      cx: sx * boardPostX,
      cy: headPostY,
      cz: headPostH * 0.5,
      sx: boardPostSz,
      sy: boardPostSz,
      sz: headPostH,
    });
  }

  // Headboard — panel at +Y end of the frame, inset between the posts.
  const headCZ = frameH + headH * 0.5;
  boxes.push({
    cx: 0,
    cy: frameL * 0.5 - headT * 0.5,
    cz: headCZ,
    sx: Math.max(0.01, frameW - boardPostSz * 2),
    sy: headT,
    sz: headH,
  });

  // Footboard
  if (hasFoot) {
    const footPostH = frameH + footH;
    const footPostY = -(frameL * 0.5 - boardPostSz * 0.5);
    for (const sx of [1, -1]) {
      boxes.push({
        cx: sx * boardPostX,
        cy: footPostY,
        cz: footPostH * 0.5,
        sx: boardPostSz,
        sy: boardPostSz,
        sz: footPostH,
      });
    }
    const footCZ = frameH + footH * 0.5;
    boxes.push({
      cx: 0,
      cy: -(frameL * 0.5 - headT * 0.5),
      cz: footCZ,
      sx: Math.max(0.01, frameW - boardPostSz * 2),
      sy: headT,
      sz: footH,
    });
  }

  return { kind: "mesh", geometry: concatBoxes(boxes) };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const BED_GENERATOR: Generator = defineGenerator<BedParams>({
  id: "bed",
  label: "Bed",
  category: "prop",
  paramSpecs: BED_SPECS,
  defaultParams: paramsFromSpecs(BED_SPECS) as BedParams,
  build: buildBed,
});
